// Lesson 1.6, Capstone: your prompt library.
// Approved by Leena 2026-10-09, converted mechanically from the draft.

export default {
  key: '1-6',
  title: 'Capstone: your prompt library',
  minutes: 15,
  status: 'ready',
  outcome: 'Five prompts for your own job, plus your closing time estimate.',

  why: 'A course is worth nothing if it does not survive contact with next Tuesday. What survives is a short list of prompts that fit jobs you actually do, saved somewhere you will find them. Five is the right number. Twenty is a thing you will never open.',

  blocks: [
    {
      type: 'concept',
      heading: 'What makes a prompt worth keeping',
      body: [
        { type: 'list', items: [
          '**It maps to a job you do repeatedly.** Weekly beats clever.',
          '**It has the four parts built in,** so you are filling blanks rather than thinking.',
          '**It carries your voice rules with it,** or lives in a tool that already knows them from lesson 1.5.',
          '**It names what not to do.** The constraints are what stop you editing the output.',
        ] },
        { type: 'p', text: 'Go back to the admin-tasks list you wrote in lesson 1.1. That list is your source material. The jobs on it that are mostly writing and shaping are the ones to turn into prompts now.' },
        { type: 'callout', tone: 'info', text: 'A reasonable five for most people: the long thing made short, the tricky reply, notes into a clean document, the thing you explain over and over, and the fact-check from lesson 1.2.' },

        { type: 'sub', text: 'Where this goes next' },
        { type: 'p', text: 'A saved prompt is a good start, but it stays still. The next step up is building something you keep sharpening: a saved setup that carries your voice rules, your examples and your constraints, which you improve a little every time you use it, so it gets better month after month instead of staying the same. That is what the next course covers. For now, five prompts that fit your week is exactly the right place to stop.' },
      ],
    },

    {
      type: 'exercise',
      heading: 'Write and save five',
      steps: [
        { id: 'open_list', text: 'Open your admin-tasks list from lesson 1.1. Pick the five jobs that are mostly writing or shaping.' },
        { id: 'write_five', text: 'Write a prompt for each, using the four-part skeleton from lesson 1.3. Save each one to your prompt library on the course page.' },
        { id: 'use_one', text: 'Use at least one of them today, on something real. Tick the box when you have.' },
        { id: 'prune_list', text: 'Go back to your admin-tasks list and remove anything that has actually gone away. If nothing has, that is worth telling us in the box below.' },
        { id: 'closing_estimate', text: 'Answer the closing time question on the course page.' },
      ],
    },

    {
      type: 'concept',
      body: [
        { type: 'callout', tone: 'info', text: 'The closing number matters more than it looks. It is how you tell your own manager, or yourself, whether this was worth doing. We also read it, which is how we work out what to build next.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Which is the better candidate for your library?',
          options: ['A clever prompt for an unusual one-off task', 'A plain prompt for something you do every week'],
          answer: 1,
          because: 'Frequency is what turns a prompt into saved time.',
        },
        {
          q: 'Why does a good saved prompt say what not to do?',
          options: ['It makes the prompt look thorough', 'The constraints are what stop you having to edit the output'],
          answer: 1,
          because: 'Banning the tells and fixing the format up front is most of the editing you would otherwise do by hand.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'Turn this into a reusable prompt',
      body: 'That worked. Turn what I just asked you into a prompt I can reuse for this kind of task. Put square brackets around the parts I will swap each time, keep the constraints that made this one come out right, and keep it short enough that I will actually use it.',
      note: 'The one that keeps the library growing after the course ends.',
    },

    {
      type: 'takeaway',
      text: 'Five prompts for jobs you actually do, saved where you will find them. That is what you keep.',
    },
  ],
};
